import React from 'react';
import { Link } from 'react-router-dom';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

const getStatusClass = (status) => {
  const statusClasses = {
    'Pending': 'tw-border-amber-200 tw-bg-amber-50 tw-text-amber-700',
    'In Progress': 'tw-border-sky-200 tw-bg-sky-50 tw-text-sky-700',
    'Solved': 'tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700',
    'Cancelled': 'tw-border-rose-200 tw-bg-rose-50 tw-text-rose-700'
  };
  return `tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${statusClasses[status] || 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-600'}`;
};

const formatDate = (dateString, timeZone) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)} hours ago`;
  } else if (diffInDays < 7) {
    return `${Math.floor(diffInDays)} days ago`;
  } else {
    return formatUtcInTimeZone(dateString, timeZone, 'MMM DD, YYYY');
  }
};

function Ticket({ tickets = [], loading = false, canViewModule = false }) {
  const { timeZone } = useTimezone();
  return (
    <div className="tw-flex tw-min-h-[500px] tw-min-w-0 tw-flex-col tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
        <h5 className="tw-mb-0">Latest Support Tickets</h5>
        {canViewModule && (
          <Link
            to="/admin/settings?activeTab=query"
            className="tw-shrink-0 tw-text-sm tw-font-medium tw-text-[#3152e8] hover:tw-underline"
            aria-label="View all support tickets"
          >
            View all
          </Link>
        )}
      </div>

      {loading ? (
        <div className="tw-flex tw-min-h-[400px] tw-items-center tw-justify-center">
          <Loader />
        </div>
      ) : tickets.length === 0 ? (
        <div className="tw-flex tw-min-h-[400px] tw-items-center tw-justify-center">
          <NoData
            message="No support tickets yet"
            description="New support requests will appear here."
            icon="mdi:ticket-outline"
          />
        </div>
      ) : (
        <div className="tw-max-h-[400px] tw-min-w-0 tw-space-y-3 tw-overflow-y-auto">
          {tickets.map((ticket, index) => (
            <div
              key={ticket.id || index}
              className="tw-min-w-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4"
            >
              <div className="tw-flex tw-min-w-0 tw-items-start tw-justify-between tw-gap-3">
                <h6 className="tw-mb-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-leading-5 tw-text-slate-900" title={ticket.subject || 'No Subject'}>
                  {ticket.subject || 'No Subject'}
                </h6>
                <span className={getStatusClass(ticket.status)}>{ticket.status || 'Unknown'}</span>
              </div>
              <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-slate-600">
                Ticket ID: {ticket.ticket_id || 'N/A'}
              </p>
              <div className="tw-mt-2 tw-text-xs tw-text-slate-500">
                {formatDate(ticket.createdAt, timeZone)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Ticket;
